import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

interface Gallery {
  title: string
  subtitle: string
  category: string
  location: string
  thumbnail: string
  keywords: string[]
  images: Array<{
    id: string
    filename: string
    thumbnail: string
    fullsize: string
  }>
}

type GalleryData = Record<string, Gallery>

export const HomePage: React.FC = () => {
  const [galleries, setGalleries] = useState<GalleryData>({})
  const [businessInfo, setBusinessInfo] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [galleriesResponse, businessResponse] = await Promise.all([
          fetch('/data/galleries.json'),
          fetch('/data/business-info.json')
        ])

        const galleriesData = await galleriesResponse.json()
        const businessData = await businessResponse.json()

        setGalleries(galleriesData)
        setBusinessInfo(businessData)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // Select featured galleries (a curated mix of wedding and engagement)
  const featuredGalleryIds = [
    'grace-cathedral',
    'san-francisco-engagement', 
    'vine-hill-house',
    'san-francisco-pier-7-surprise-proposal',
    'stanford-memorial-church',
    'willow-heights-mansion'
  ]

  const featuredGalleries = featuredGalleryIds
    .map(id => [id, galleries[id]])
    .filter(([_, gallery]) => gallery)

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-7xl font-light text-gray-900 mb-6 tracking-wide">
            {businessInfo?.businessName || 'Duende Photo'}
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 font-light tracking-wide">
            {businessInfo?.tagline || 'Fine Art Wedding Photography'}
          </p>
          <p className="text-lg text-gray-500 mb-12 max-w-2xl mx-auto leading-relaxed">
            Serving the {businessInfo?.location || 'San Francisco Bay Area'} since {businessInfo?.established || '2005'}
          </p>
          
          {/* Philosophy Quote */}
          {businessInfo?.philosophy && (
            <div className="max-w-3xl mx-auto mb-12">
              <blockquote className="text-lg italic text-gray-700 leading-relaxed">
                "{businessInfo.philosophy.quote}"
              </blockquote>
              <cite className="text-sm text-gray-500 mt-4 block">
                — {businessInfo.philosophy.author}
              </cite>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/portfolio"
              className="px-8 py-3 bg-gray-900 text-white font-light tracking-wide hover:bg-gray-800 transition-colors duration-300"
            >
              View Full Portfolio
            </Link>
            <Link
              to="/quote"
              className="px-8 py-3 border border-gray-900 text-gray-900 font-light tracking-wide hover:bg-gray-900 hover:text-white transition-colors duration-300"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Work Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 tracking-wide">
              Featured Work
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              A curated selection showcasing our fine art approach to wedding and engagement photography
            </p>
            <Link
              to="/portfolio"
              className="text-gray-600 hover:text-gray-900 underline font-light"
            >
              View Complete Portfolio →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredGalleries.slice(0, 6).map(([galleryId, gallery]: [string, Gallery]) => (
              <Link
                key={galleryId}
                to={`/gallery/${galleryId}`}
                className="group block"
              >
                <div className="aspect-[4/5] overflow-hidden bg-gray-100">
                  <img
                    src={gallery.thumbnail}
                    alt={gallery.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-light text-gray-900 mb-1 group-hover:text-gray-700 transition-colors duration-200">
                    {gallery.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-1">
                    {gallery.subtitle}
                  </p>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    {gallery.category} • {gallery.location}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4 tracking-wide">
              Our Services
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white shadow-sm">
              <h3 className="text-xl font-light text-gray-900 mb-4">Wedding Photography</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Complete wedding day coverage with our signature fine art style, 
                capturing every precious moment from preparation to celebration.
              </p>
              <Link to="/packages" className="text-gray-600 hover:text-gray-900 underline text-sm">
                View Packages →
              </Link>
            </div>
            
            <div className="text-center p-6 bg-white shadow-sm">
              <h3 className="text-xl font-light text-gray-900 mb-4">Engagement Sessions</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Intimate engagement photography sessions in beautiful Bay Area locations, 
                perfect for save-the-dates and engagement announcements.
              </p>
              <Link to="/portfolio" className="text-gray-600 hover:text-gray-900 underline text-sm">
                View Engagement Galleries →
              </Link>
            </div>
            
            <div className="text-center p-6 bg-white shadow-sm">
              <h3 className="text-xl font-light text-gray-900 mb-4">Fine Art Portraits</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Artistic bridal portraits and specialty photography, including our unique 
                "Wedding Dresses in Motion" sessions.
              </p>
              <Link to="/gallery/wedding-dresses-in-motion" className="text-gray-600 hover:text-gray-900 underline text-sm">
                Learn More →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Awards Section */}
      {businessInfo?.awards && businessInfo.awards.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-2xl font-light text-gray-900 mb-8">
              Recognition
            </h2>
            <div className="flex flex-wrap justify-center gap-8">
              {businessInfo.awards.map((award: any, index: number) => (
                <div key={index} className="text-center">
                  <p className="text-lg font-light text-gray-900">{award.title}</p>
                  <p className="text-sm text-gray-600">{award.organization}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Ready to Tell Your Story?
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Let's discuss how we can capture the beauty and emotion of your special day 
            with our distinctive fine art approach.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/quote"
              className="px-8 py-3 bg-gray-900 text-white font-light tracking-wide hover:bg-gray-800 transition-colors duration-300"
            >
              Request a Quote
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3 border border-gray-900 text-gray-900 font-light tracking-wide hover:bg-gray-900 hover:text-white transition-colors duration-300"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
